import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, LogOut, Building2, UserCheck, CreditCard, ArrowRight } from 'lucide-react';
import './SuperadminDashboard.css';

const SuperadminDashboard = () => {
    const navigate = useNavigate();
    const currentUser = localStorage.getItem('currentUser') || 'Super Admin';
    const [companyCount, setCompanyCount] = useState(0);
    const [adminCount, setAdminCount] = useState(0);
    const [planCount, setPlanCount] = useState(0);

    const serverUrl = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:8080';

    useEffect(() => {
        const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
        if (userRole !== 'superadmin') {
            navigate('/admin-dashboard', { replace: true });
            return;
        }
        fetchDashboardData();
    }, [navigate]);

    const fetchDashboardData = async () => {
        try {
            const [compRes, adminRes, planRes] = await Promise.all([
                fetch(`${serverUrl}/api/companies`),
                fetch(`${serverUrl}/api/admin-users`),
                fetch(`${serverUrl}/api/subscription-plans`)
            ]);

            if (compRes.ok) {
                const compData = await compRes.json();
                setCompanyCount(compData.length);
            }
            if (adminRes.ok) {
                const adminData = await adminRes.json();
                setAdminCount(adminData.length);
            }
            if (planRes.ok) {
                const planData = await planRes.json();
                setPlanCount(planData.length);
            }
        } catch (err) {
            console.log('Error fetching superadmin counts, using fallbacks');
            setCompanyCount(1);
            setAdminCount(1);
            setPlanCount(2);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    return (
        <div className="dashboard-page-container">
            {/* Top Navigation */}
            <motion.nav
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="dashboard-nav glass-panel"
            >
                <div className="nav-brand">
                    <div className="brand-icon">
                        <Crown size={22} color="var(--primary-color)" />
                    </div>
                    <h2 className="text-gradient" style={{ fontSize: '20px', margin: 0, fontWeight: 700 }}>Superadmin Console</h2>
                </div>
                <div className="nav-user">
                    <span className="role-badge superadmin">{currentUser}</span>
                    <button onClick={handleLogout} className="btn-secondary" style={{ padding: '8px 16px' }}>
                        <LogOut size={16} /> Logout
                    </button>
                </div>
            </motion.nav>

            {/* Dashboard Tiles Grid */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="stats-grid"
            >
                {/* Tile 1: Company Setup */}
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="superadmin-tile glass-panel"
                    onClick={() => navigate('/superadmin/companies')}
                >
                    <div className="superadmin-tile-header">
                        <div className="stat-icon">
                            <Building2 size={26} />
                        </div>
                        <span className="tile-badge">{companyCount} Companies</span>
                    </div>

                    <div className="tile-content">
                        <h3 className="tile-title">Company Setup</h3>
                    </div>

                    <div className="tile-action-link">
                        Manage Companies <ArrowRight size={16} />
                    </div>
                </motion.div>

                {/* Tile 2: Admin User Accounts */}
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="superadmin-tile glass-panel"
                    onClick={() => navigate('/superadmin/admins')}
                >
                    <div className="superadmin-tile-header">
                        <div className="stat-icon fe-icon">
                            <UserCheck size={26} />
                        </div>
                        <span className="tile-badge fe-badge">
                            {adminCount} Admins
                        </span>
                    </div>

                    <div className="tile-content">
                        <h3 className="tile-title">User Accounts</h3>
                    </div>

                    <div className="tile-action-link fe-link">
                        Provision Users <ArrowRight size={16} />
                    </div>
                </motion.div>

                {/* Tile 3: Subscription Plans */}
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="superadmin-tile glass-panel"
                    onClick={() => navigate('/superadmin/subscription-plans')}
                >
                    <div className="superadmin-tile-header">
                        <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669', borderColor: 'rgba(16, 185, 129, 0.2)' }}>
                            <CreditCard size={26} />
                        </div>
                        <span className="tile-badge" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669', borderColor: 'rgba(16, 185, 129, 0.25)' }}>
                            {planCount} Plans
                        </span>
                    </div>

                    <div className="tile-content">
                        <h3 className="tile-title">Subscription Plans</h3>
                    </div>

                    <div className="tile-action-link" style={{ color: '#059669' }}>
                        Manage Plans <ArrowRight size={16} />
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default SuperadminDashboard;
